import { readState } from "@/__main__/app-state.mjs";
import { IS_NODE } from "@/__main__/constants.mjs";
import createModel from "@/__main__/data-model.mjs";
import getData from "@/__main__/get-data.mjs";
import { appURLs } from "@/app/constants.mjs";
import { useClientOnly } from "@/util/ClientOnly.jsx";
import { devError } from "@/util/dev.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const countryCodeCache = {
  current: null as string | null,
};

// synchronously use the cached value if available
export function readCountryCode(): string | null {
  if (!countryCodeCache.current)
    devError(
      "Tried to get country code before it was fetched. fetchCountryCode() first!",
    );
  return countryCodeCache.current;
}

const useCountryCode = (): string | null => {
  const {
    search: { regionData },
  } = useSnapshot(readState);

  // We only want to fetch the country code on the client, and we need to wait for hydration
  const isHydrated = useClientOnly();
  if (!isHydrated) return;

  return regionData["ISO 3166-1 alpha-2"];
};

// this path is mostly arbitrary. we can't put this in  a u t h  because it's a feature,
// search is the only other module that uses country code
const COUNTRY_CODE_PATH = ["search", "regionData"] as const;

// read from the persistent store if available. db entry expires after 24 hours
export async function fetchCountryCode(
  fetchOptions?: Arguments<typeof getData>[3],
): Promise<string | null> {
  // no-op on server
  if (IS_NODE) return "US";

  const COUNTRY_CODE_PROVIDER = `${appURLs.BLITZ}/geo-ip`;

  const { "ISO 3166-1 alpha-2": countryCode } = await getData(
    COUNTRY_CODE_PROVIDER,
    CountryCodeModel,
    COUNTRY_CODE_PATH,
    {
      // We are gonna assume this doesn't change much...
      shouldFetchIfPathExists: false,
      expiryTime: Date.now() + 2 * 24 * 60 * 60 * 1000, // 2 days
      ...fetchOptions,
    },
  );

  countryCodeCache.current = countryCode; // assign to cache for synchronous use

  return countryCode;
}

const CountryCodeModel = createModel({
  remoteAddress: String,
  "ISO 3166-1 alpha-2": String,
});

export default useCountryCode;
